import { z } from "zod"

const notificationRouteParamValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
])

export const notificationCommandNameSchema = z.enum(["refresh_notifications"])
export const notificationPayloadKindSchema = z.enum([
  "gw.roster_published",
  "system.announcement",
])

export const notificationActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("none"),
  }),
  z.object({
    type: z.literal("route"),
    to: z.string().trim().min(1),
    params: z.record(z.string(), notificationRouteParamValueSchema).optional(),
    search: z.record(z.string(), notificationRouteParamValueSchema).optional(),
    label: z.string().trim().min(1).optional(),
  }),
  z.object({
    type: z.literal("open_url"),
    href: z.url(),
    external: z.boolean().optional(),
    label: z.string().trim().min(1).optional(),
  }),
  z.object({
    type: z.literal("command"),
    name: notificationCommandNameSchema,
    args: z.record(z.string(), z.unknown()).optional(),
    label: z.string().trim().min(1).optional(),
  }),
])

const notificationActionWithDefaultSchema = notificationActionSchema.default({
  type: "none",
})

const gwRosterPublishedPayloadSchema = z.object({
  kind: z.literal("gw.roster_published"),
  title: z.string().trim().min(1),
  body: z.string().trim().min(1),
  data: z.object({
    rosterDate: z.iso.date(),
    sourceWeekId: z.string().trim().min(1).nullable().optional(),
  }),
  action: notificationActionWithDefaultSchema,
})

const systemAnnouncementPayloadSchema = z.object({
  kind: z.literal("system.announcement"),
  title: z.string().trim().min(1),
  body: z.string().trim().min(1),
  data: z.object({
    level: z.enum(["info", "warning", "critical"]),
  }),
  action: notificationActionWithDefaultSchema,
})

export const notificationPayloadSchema = z.discriminatedUnion("kind", [
  gwRosterPublishedPayloadSchema,
  systemAnnouncementPayloadSchema,
])

export const appNotificationSchema = z
  .object({
    id: z.uuid(),
    userId: z.string().trim().min(1),
    kind: notificationPayloadKindSchema,
    payload: notificationPayloadSchema,
    readAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    dedupeKey: z.string().trim().min(1).nullable(),
  })
  .superRefine((value, context) => {
    if (value.kind !== value.payload.kind) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Notification kind must match payload kind",
        path: ["kind"],
      })
    }
  })

export type NotificationAction = z.infer<typeof notificationActionSchema>
export type NotificationCommandName = z.infer<
  typeof notificationCommandNameSchema
>
export type NotificationPayloadInput = z.input<typeof notificationPayloadSchema>
export type NotificationPayload = z.infer<typeof notificationPayloadSchema>
export type AppNotification = z.infer<typeof appNotificationSchema>
